import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { AlertCircle, Eye, EyeOff, Loader2, Shield } from 'lucide-react'

interface LoginHeaderProps {
  t: TFunction<'auth'>
}

export function LoginHeader({ t }: LoginHeaderProps): ReactElement {
  return (
    <div className="mb-8 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-lime-500">
        <Shield className="h-8 w-8 text-stone-900" />
      </div>
      <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100">
        {t('login.title')}
      </h1>
      <p className="mt-2 text-stone-600 dark:text-stone-400">{t('login.subtitle')}</p>
    </div>
  )
}

interface ErrorAlertProps {
  message: string
}

export function ErrorAlert({ message }: ErrorAlertProps): ReactElement {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-400">
      <AlertCircle className="h-5 w-5 shrink-0" />
      <p className="text-sm">{message}</p>
    </div>
  )
}

const INPUT_CLASSES =
  'w-full rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-stone-900 placeholder-stone-400 focus:border-lime-500 focus:ring-2 focus:ring-lime-500/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-stone-700 dark:bg-stone-800 dark:text-stone-100 dark:placeholder-stone-500 dark:focus:border-lime-400'

interface EmailFieldProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
  t: TFunction<'auth'>
}

export function EmailField({ value, onChange, disabled, t }: EmailFieldProps): ReactElement {
  return (
    <div>
      <label
        htmlFor="email"
        className="mb-2 block text-sm font-medium text-stone-700 dark:text-stone-300"
      >
        {t('login.emailLabel')}
      </label>
      <input
        type="email"
        id="email"
        name="email"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder={t('login.emailPlaceholder')}
        autoComplete="email"
        required
        disabled={disabled}
        className={INPUT_CLASSES}
      />
    </div>
  )
}

interface PasswordFieldProps {
  value: string
  onChange: (value: string) => void
  showPassword: boolean
  onToggleShow: () => void
  disabled: boolean
  t: TFunction<'auth'>
}

export function PasswordField({
  value,
  onChange,
  showPassword,
  onToggleShow,
  disabled,
  t,
}: PasswordFieldProps): ReactElement {
  return (
    <div>
      <label
        htmlFor="password"
        className="mb-2 block text-sm font-medium text-stone-700 dark:text-stone-300"
      >
        {t('login.passwordLabel')}
      </label>
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          id="password"
          name="password"
          value={value}
          onChange={(e) => {
            onChange(e.target.value)
          }}
          placeholder={t('login.passwordPlaceholder')}
          autoComplete="current-password"
          required
          disabled={disabled}
          className={`${INPUT_CLASSES} pr-12`}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute top-1/2 right-3 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
        >
          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  )
}

interface SubmitButtonProps {
  isLoading: boolean
  t: TFunction<'auth'>
}

export function SubmitButton({ isLoading, t }: SubmitButtonProps): ReactElement {
  return (
    <button
      type="submit"
      disabled={isLoading}
      className="flex w-full items-center justify-center gap-2 rounded-lg bg-lime-500 px-4 py-2.5 font-medium text-stone-900 transition-colors hover:bg-lime-400 focus:ring-2 focus:ring-lime-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-stone-900"
    >
      {isLoading ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          {t('login.submittingButton')}
        </>
      ) : (
        t('login.submitButton')
      )}
    </button>
  )
}

export function DevCredentialsHint(): ReactElement {
  return (
    <div className="mt-4 rounded-lg border border-stone-200 bg-stone-100 p-4 dark:border-stone-800 dark:bg-stone-900">
      <p className="text-xs text-stone-500 dark:text-stone-400">
        <strong>Development:</strong> Use{' '}
        <code className="rounded bg-stone-200 px-1 dark:bg-stone-800">dev@evidenx.local</code> /{' '}
        <code className="rounded bg-stone-200 px-1 dark:bg-stone-800">EvidenX123</code>
      </p>
    </div>
  )
}
